import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, switchMap, tap } from 'rxjs';
import { PageService } from 'src/app/shared/page.service';
import { translationsCustomization } from 'src/app/translations/customization/translations-customization.const';
import { environment } from 'src/environments/environment';

import {
  AvatarPositionEnum,
  ChatConversation,
  ChatHeader,
  ClosedConversation,
  CustomizationLog,
  defaultAlcmeonImage,
  defaultBrandMessageAvatar,
  defaultPrivacyLinkColor,
  defaultSubtitleColor,
  defaultTitleColor,
  defaultTitleLogo,
  InMessageLanguageEnum,
} from '../shared';
import {
  defaultAlcmeonColorPrimary,
  defaultCustomerMessageBackgroundColor,
  defaultCustomerMessageColor,
} from '../shared/chat-conversation-default-values.const';
import { AlertingService } from './alerting.service';
import { TranslateCustomizationService } from './translate-customization.service';

@Injectable({
  providedIn: 'root',
})
export class CustomizationService {
  customization = false;
  customizationDisplayed = true;
  chatCustomizationBS: BehaviorSubject<{
    mbr: ClosedConversation;
    chatHeaderData: ChatHeader;
    chatConversationData: ChatConversation;
  }> = new BehaviorSubject(null);
  snippetBS: BehaviorSubject<any> = new BehaviorSubject(null);
  pageLanguageBS: BehaviorSubject<any> = new BehaviorSubject(null);
  chatPositionBS: BehaviorSubject<'bottom-left-corner'> = new BehaviorSubject(null);
  advancedCustomization = false;
  pageLanguage: InMessageLanguageEnum = InMessageLanguageEnum.ENGLISH;
  importInProgressBS: BehaviorSubject<boolean> = new BehaviorSubject(false);

  _customizationLanguage: InMessageLanguageEnum;
  get customizationLanguage(): InMessageLanguageEnum {
    return this._customizationLanguage;
  }
  set customizationLanguage(value) {
    if (
      !value ||
      [
        InMessageLanguageEnum.ENGLISH,
        InMessageLanguageEnum.FRENCH,
        InMessageLanguageEnum.GERMAN,
        InMessageLanguageEnum.ITALIAN,
        InMessageLanguageEnum.SPANISH,
      ].indexOf(value) < 0
    ) {
      value = InMessageLanguageEnum.ENGLISH;
    }
    this._customizationLanguage = value;
  }

  pageId: number;
  email: string;
  pageCustomizationLogId: number;
  position: AvatarPositionEnum;

  closedConversationData: ClosedConversation;
  chatHeaderData: ChatHeader;
  chatConversationData: ChatConversation;

  chatHeaderStyleCustomized: HTMLStyleElement;
  chatConversationStyleCustomized: HTMLStyleElement;
  closedConversationStyleCustomized: HTMLStyleElement;

  private helperMessage1Element;
  private helperMessage2Element;
  private helperMessage3Element;
  private helperMessage4Element;

  private helperContainer: HTMLDivElement;

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private alertingService: AlertingService,
    private pageService: PageService,
    private translateCustomizationService: TranslateCustomizationService
  ) {}

  loadCustomization(pageId: string, email: string) {
    this.pageId = +pageId.replace('p_', '');
    this.email = email;

    document.querySelectorAll('.customization app-chat').forEach(element => (element['style'].display = 'none'));

    return this.httpClient.get(`${environment.apiURLPrefix}customization/${pageId}?email=${email}`).pipe(
      tap((result: CustomizationLog) => {
        this.pageCustomizationLogId = result.id;
        this.updatePageLanguageAndPosition(result);
      })
    );
  }

  private updatePageLanguageAndPosition(result: Partial<CustomizationLog>) {
    this.pageLanguage = result.chat_header?.lang || InMessageLanguageEnum.ENGLISH;
    this.pageLanguageBS.next(this.pageLanguage);
    this.position = result.closed_conversation?.position || AvatarPositionEnum.BOTTOM_RIGHT_CORNER;
  }

  saveCustomization() {
    const data = this.formatThemeAsJson();
    this.httpClient
      .post(`${environment.apiURLPrefix}customization/p_${this.pageId}/log/${this.pageCustomizationLogId}`, data)
      .subscribe({
        next: async () => {
          const message = await this.instant('customization/common/save-success');
          this.alertingService.success(message);
        },
        error: async () => {
          const message = await this.instant('customization/common/save-error');
          this.alertingService.error(message);
        },
      });
  }

  private formatThemeAsJson(): Partial<CustomizationLog> {
    const { helperMessage1, helperMessage2, helperMessage3, helperMessage4 } = this.closedConversationData;
    const data: Partial<CustomizationLog> = {
      closed_conversation: {
        ...this.closedConversationData,
        helperMessage1: this.asHtml(helperMessage1),
        helperMessage2: this.asHtml(helperMessage2),
        helperMessage3: this.asHtml(helperMessage3),
        helperMessage4: this.asHtml(helperMessage4),
      },
      chat_header: { ...this.chatHeaderData, lang: this.pageLanguage },
      chat_conversation: this.chatConversationData,
    };

    const removePropertyIfEmpty = column => {
      Object.keys(data[column]).forEach(key => {
        if (!data[column][key]) {
          delete data[column][key];
        }
      });
    };
    removePropertyIfEmpty('closed_conversation');
    removePropertyIfEmpty('chat_conversation');
    removePropertyIfEmpty('chat_header');
    return data;
  }

  exportSnippet() {
    const data = this.computeSnippet();
    this.exportToFile(`alcmeon-in-message-${this.pageId}-${this.pageLanguage}.js`, data, 'application/text');
  }

  exportCustomization() {
    const data = this.formatThemeAsJson();
    this.exportToFile(
      `alcmeon-theme-in-message-${this.pageId}-${this.pageLanguage}.json`,
      JSON.stringify(data),
      'application/json'
    );
  }

  updateCustomization(data: Partial<CustomizationLog>) {
    const { chat_header, closed_conversation, chat_conversation } = data;
    this.chatHeaderData = chat_header || this.initDefaultChatHeaderDataData();
    this.closedConversationData = closed_conversation || this.initDefaultClosedConversationData();
    this.chatConversationData = chat_conversation || this.initDefaultChatConversationData();
  }

  applyCustomizationFromJson(data: Partial<CustomizationLog>) {
    this.importInProgressBS.next(true);
    this.updatePageLanguageAndPosition(data);
    this.chatHeaderData = null;
    this.closedConversationData = null;
    this.chatConversationData = null;
    this.updateCustomization(data);
    if (!data || !Object.keys(data.closed_conversation)?.length) {
      this.initDefaultData();
    }
    this.refreshWidgetPreview();
    if (data.closed_conversation.position === 'bottom-left-corner') {
      this.chatPositionBS.next('bottom-left-corner');
    }
    setTimeout(() => this.importInProgressBS.next(false), 1000);
  }

  computeSnippet() {
    if (!this.closedConversationData || !this.chatHeaderData || !this.chatConversationData) {
      console.error('Something went wrong');

      return;
    }

    if (!document.querySelector('#alcmeon-inmessage-toggle-button')) {
      this.alterCloseChatConversation();
    }
    const data = `<script>
  window.alcmeonInMessageConfig = {
    langCode: '${this.pageLanguage || InMessageLanguageEnum.ENGLISH}',
    pageId: 'p_${this.pageId}'
  };

  (function(d, s, id){
    var js, fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) {return;}
    js = d.createElement(s); js.id = id;
    js.src = "https://inmessage.alcmeon.com/prod/client/latest/mbr-v2.js";
    fjs.parentNode.insertBefore(js, fjs);
  }(document, 'script', 'alcmeon-inmessage'));
</script>`;

    this.snippetBS.next(data);
    return data;
  }

  private exportToFile(
    filename: string,
    data: string,
    type: 'application/text' | 'application/javascript' | 'application/json'
  ) {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  refreshWidgetPreview() {
    this.chatCustomizationBS.next({
      mbr: this.closedConversationData,
      chatHeaderData: this.chatHeaderData,
      chatConversationData: this.chatConversationData,
    });
    this.computeSnippet();
  }

  instant = async (key: string) => {
    const translateService = await this.translateCustomizationService.loadLanguage(this.customizationLanguage);
    translateService.setTranslation(
      this.customizationLanguage,
      translationsCustomization.get(this.customizationLanguage),
      true
    );
    return translateService.instant(key);
  };

  alterCloseChatConversation() {
    this.addToggleButton();

    const myDiv = document.querySelector('.close-btn');

    if (myDiv) {
      myDiv.addEventListener('click', () => {
        document.querySelector('.customization app-chat')['style'].display = 'none';
        if (!this.checkIfHelperShow() && this.helperContainer) {
          this.listenOngoingConversationHelperThenClean();
        }
      });
    }
  }

  private addToggleButton() {
    const toggleButton = document.createElement('button');
    toggleButton.addEventListener('click', () => {
      document.querySelector('.customization app-chat')['style'].display = 'block';
      window.postMessage({ type: 'becomesVisible' }, '*');
    });
    toggleButton.setAttribute('type', 'button');
    toggleButton.setAttribute('id', 'alcmeon-inmessage-toggle-button');
    toggleButton.style.setProperty('background-color', 'inherit');
    toggleButton.style.setProperty('border', 'none');
    document.querySelector('.customization').appendChild(toggleButton);
    const { helperMessage1, helperMessage2 } = this.closedConversationData;
    if (helperMessage1 || helperMessage2) {
      this.helperContainer = document.createElement('div');
      this.helperContainer.classList.add('helper-container');
      this.helperContainer.classList.add('new-conversation');
      this.helperContainer.setAttribute('aria-label', "the chat's helper");

      const closeHelperElement = document.createElement('button');
      closeHelperElement.setAttribute('aria-label', "close the chat's helper");
      closeHelperElement.classList.add('close-helper');
      closeHelperElement.addEventListener('click', () => {
        if (!this.checkIfHelperShow()) {
          this.helperContainer.classList.add('helper-dismiss');
        }
      });
      this.helperContainer.appendChild(closeHelperElement);

      this.helperContainer.setAttribute('aria-label', "the chat's helper");
      if (helperMessage1) {
        this.helperMessage1Element = this.insertMessageHelper(helperMessage1);
        this.helperContainer.appendChild(this.helperMessage1Element);
      }
      if (helperMessage2) {
        this.helperMessage2Element = this.insertMessageHelper(helperMessage2);
        this.helperContainer.appendChild(this.helperMessage2Element);
      }
      document.body.appendChild(this.helperContainer);
      setTimeout(() => {
        if (!this.checkIfHelperShow()) {
          this.helperContainer?.classList.add('helper-visible');
        }
        setTimeout(() => {
          if (!this.checkIfHelperShow()) {
            this.helperContainer?.classList.add('helper-dismiss');
          }
        }, 10000);
      }, 3000);
    }
  }

  cleanDOM() {
    const removeChild = element => {
      try {
        if (element && document.body.contains(element)) {
          document.body.removeChild(element);
        }
      } catch {
        // Do nothing
      }
    };

    removeChild(this.chatHeaderStyleCustomized);
    removeChild(this.chatConversationStyleCustomized);
    removeChild(this.closedConversationStyleCustomized);
    if (this.helperContainer) {
      this.helperContainer.querySelector('.close-helper').removeEventListener('click', this.closeHelperCallback);
      removeChild(this.helperContainer);
    }
    document.body.style.removeProperty('background-image');
  }

  updateHelperMessage(helperType: 'new-conversation' | 'ongoing-conversation', id: number, message) {
    const elements = document.querySelectorAll(`.helper-container.${helperType} .helper-message`);
    if (elements?.length) {
      const element = elements[1 - (id % 2)];
      if (element) {
        element.innerHTML = message;
      }
    }
  }

  toggledHelperMessage(helperType: 'new-conversation' | 'ongoing-conversation', helperMessagesActive) {
    this.cleanMessageHelpers();
    if (helperType === 'new-conversation') {
      this.updateNewConversationHelperMessages(helperMessagesActive);
    } else if (helperType === 'ongoing-conversation') {
      this.helperContainer.querySelector('.close-helper').removeEventListener('click', this.closeHelperCallback);
      this.listenOngoingConversationHelperThenClean(helperMessagesActive);
    }
  }

  private cleanMessageHelpers() {
    if (this.helperMessage1Element) {
      this.helperContainer.removeChild(this.helperMessage1Element);
      this.helperMessage1Element = null;
    }
    if (this.helperMessage2Element) {
      this.helperContainer.removeChild(this.helperMessage2Element);
      this.helperMessage2Element = null;
    }
    if (this.helperMessage3Element) {
      this.helperContainer.removeChild(this.helperMessage3Element);
      this.helperMessage3Element = null;
    }
    if (this.helperMessage4Element) {
      this.helperContainer.removeChild(this.helperMessage4Element);
      this.helperMessage4Element = null;
    }
  }

  private checkIfHelperShow() {
    return this.helperContainer?.classList.contains('show');
  }

  private updateNewConversationHelperMessages(helperMessagesActive?) {
    const { helperMessage1, helperMessage2 } = this.closedConversationData;
    let firstMessageActive = true;
    let secondMessageActive = true;
    if (helperMessagesActive) {
      firstMessageActive = helperMessagesActive[1];
      secondMessageActive = helperMessagesActive[2];
    }
    if (helperMessage1 || helperMessage2) {
      if (!this.helperContainer) {
        this.helperContainer = document.createElement('div');
        this.helperContainer.classList.add('helper-container');
        this.helperContainer.classList.add('new-conversation');
        this.helperContainer.setAttribute('aria-label', "the chat's helper");
        document.body.appendChild(this.helperContainer);
      }

      if (this.helperMessage1Element) {
        this.helperContainer.removeChild(this.helperMessage1Element);
        this.helperMessage1Element = null;
      }
      if (this.helperMessage2Element) {
        this.helperContainer.removeChild(this.helperMessage2Element);
        this.helperMessage2Element = null;
      }
      if (firstMessageActive && helperMessage1) {
        this.helperMessage1Element = this.helperContainer.appendChild(this.insertMessageHelper(helperMessage1));
      }
      if (secondMessageActive && helperMessage2) {
        this.helperMessage2Element = this.helperContainer.appendChild(this.insertMessageHelper(helperMessage2));
      }
    }
  }

  private listenOngoingConversationHelperThenClean(helperMessagesActive?) {
    const { helperMessage3, helperMessage4 } = this.closedConversationData;
    let firstMessageActive = true;
    let secondMessageActive = true;
    if (helperMessagesActive) {
      firstMessageActive = helperMessagesActive[3];
      secondMessageActive = helperMessagesActive[4];
    }
    if (helperMessage3 || helperMessage4) {
      this.helperContainer.classList.remove('new-conversation');
      this.helperContainer.classList.add('ongoing-conversation');
      this.helperContainer.classList.add('show');

      if (this.helperMessage1Element) {
        this.helperContainer.removeChild(this.helperMessage1Element);
        this.helperMessage1Element = null;
      }
      if (this.helperMessage2Element) {
        this.helperContainer.removeChild(this.helperMessage2Element);
        this.helperMessage2Element = null;
      }
      if (firstMessageActive && helperMessage3) {
        this.helperMessage3Element = this.helperContainer.appendChild(this.insertMessageHelper(helperMessage3));
      }
      if (secondMessageActive && helperMessage4) {
        this.helperMessage4Element = this.helperContainer.appendChild(this.insertMessageHelper(helperMessage4));
      }

      setTimeout(() => {
        this.helperContainer.classList.remove('helper-dismiss');
        this.helperContainer.classList.remove('helper-visible');
      }, 500); // Assure all animations are done.
      this.helperContainer.querySelector('.close-helper').addEventListener('click', this.closeHelperCallback);
    }
  }

  private closeHelperCallback = () => {
    document.body.removeChild(this.helperContainer);
    this.helperContainer = null;
  };

  private insertMessageHelper(textContent) {
    const messageElement = document.createElement('div');
    messageElement.classList.add('helper-message');
    messageElement.innerHTML = textContent;
    return messageElement;
  }

  initDefaultData() {
    this.closedConversationData = this.initDefaultClosedConversationData();
    this.chatHeaderData = this.initDefaultChatHeaderDataData();
    this.chatConversationData = this.initDefaultChatConversationData();
  }

  initDefaultClosedConversationData(): ClosedConversation {
    return {
      avatar: defaultAlcmeonImage,
      position: AvatarPositionEnum.BOTTOM_RIGHT_CORNER,
      helperMessage1: this.translateService.instant('helperMessage1'),
      helperMessage2: this.translateService.instant('helperMessage2'),
      helperMessage3: this.translateService.instant('helperMessage3'),
      helperMessage4: this.translateService.instant('helperMessage4'),
      css: '',
      additionalCSS: '',
    };
  }

  initDefaultChatHeaderDataData(): ChatHeader {
    return {
      logo: defaultTitleLogo,
      title: this.translateService.instant('title'),
      subtitle: this.translateService.instant('subtitle'),
      privacyLink: '',
      privacyLinkPrefix: this.translateService.instant('privacy_link_prefix'),
      privacyLinkLabel: this.translateService.instant('privacy_link_label'),
      privacyLinkColor: defaultPrivacyLinkColor,
      titleColor: defaultTitleColor,
      subtitleColor: defaultSubtitleColor,
      css: '',
      additionalCSS: '',
    };
  }

  initDefaultChatConversationData(): ChatConversation {
    return {
      avatar: defaultBrandMessageAvatar,
      alcmeonColorPrimary: defaultAlcmeonColorPrimary,
      alcmeonColorSecondary: defaultAlcmeonColorPrimary,
      customerMessageColor: defaultCustomerMessageColor,
      customerMessageBackgroundColor: defaultCustomerMessageBackgroundColor,
      inputTextLabel: this.translateService.instant('send_a_message'),
      inputPhoneLabel: this.translateService.instant('send_your_phone'),
      inputMailLabel: this.translateService.instant('send_your_email'),
      selectOptionLabel: this.translateService.instant('select_an_option'),
      css: '',
      additionalCSS: '',
    };
  }

  uploadBackgroundImage(file: File) {
    const endpoint = `${environment.apiURLPrefix}p_${this.pageId}/demo-background-image`;
    if (!file) {
      const req = new HttpRequest('DELETE', endpoint);
      return this.httpClient
        .request(req)
        .pipe(switchMap(() => this.pageService.getDemoBackgroundImage()))
        .subscribe();
    }
    const formData = new FormData();
    formData.set('file_name', file.name);
    formData.set('file_content', file);

    const req = new HttpRequest('POST', endpoint, formData);
    return this.httpClient
      .request(req)
      .pipe(switchMap(() => this.pageService.getDemoBackgroundImage()))
      .subscribe();
  }

  asHtml = (value: string) => (value || '').replace(/\n/g, '<br/>').replace(/\*(.*?)\*/g, '<strong>$1</strong>');
  asMarkdown = (value: string) =>
    (value || '').replace(/(<br\/>|<br \/>|<br\/ >)/g, '\n').replace(/(<strong>)(.*?)(<\/strong>)/g, '*$2*');
}
